import { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  Filler,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));
const VOICED_THRESHOLD_HZ = 10;

function interpolateRawVoicedAtTime(xs, ys, xTarget) {
  if (!xs?.length || !ys?.length || !Number.isFinite(Number(xTarget))) {
    return null;
  }

  const target = Number(xTarget);
  const count = Math.min(xs.length, ys.length);

  for (let i = 0; i < count - 1; i++) {
    const x1 = Number(xs[i]);
    const x2 = Number(xs[i + 1]);

    if (!Number.isFinite(x1) || !Number.isFinite(x2)) continue;
    if (target < x1 || target > x2) continue;

    const y1 = ys[i] == null ? null : Number(ys[i]);
    const y2 = ys[i + 1] == null ? null : Number(ys[i + 1]);

    // Hide the ball in silence/unvoiced gaps instead of bridging them.
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;

    const span = x2 - x1;
    const fraction = span === 0 ? 0 : (target - x1) / span;

    return {
      x: target,
      y: y1 + (y2 - y1) * fraction,
    };
  }

  return null;
}

function makeCursorBallPlugin() {
  return {
    id: "cursorBallPlugin",
    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = 12,
        showBall = true,
        isMobile = false,
      } = pluginOptions || {};

      if (cursorX == null) return;

      const { ctx, chartArea, scales } = chart;
      const xScale = scales?.x;
      const yScale = scales?.y;
      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { left, right, top, bottom } = chartArea;

      const xC = xScale.getPixelForValue(cursorX);
      if (Number.isFinite(xC) && xC >= left && xC <= right) {
        ctx.save();

        ctx.strokeStyle = "rgba(255,255,255,0.90)";
        ctx.lineWidth = isMobile ? 3 : 3.5;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.strokeStyle = "rgba(16,185,129,1)";
        ctx.lineWidth = isMobile ? 1.6 : 1.8;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.restore();
      }

      if (
        !showBall ||
        !cursorEnvelopePoint ||
        !Number.isFinite(Number(cursorEnvelopePoint.x)) ||
        !Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        return;
      }

      const r = ballRadius ?? (isMobile ? 8.5 : 12);
      const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
      const py = yScale.getPixelForValue(cursorEnvelopePoint.y);

      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      if (px < left || px > right || py < top || py > bottom) return;

      ctx.save();

      ctx.shadowColor = "rgba(16,185,129,0.48)";
      ctx.shadowBlur = isMobile ? 14 : 18;
      ctx.beginPath();
      ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(16,185,129,0.95)";
      ctx.fill();

      ctx.shadowBlur = 0;

      const bodyGrad = ctx.createRadialGradient(
        px - r * 0.32,
        py - r * 0.34,
        r * 0.18,
        px,
        py,
        r
      );
      bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
      bodyGrad.addColorStop(0.2, "rgba(134,239,172,1)");
      bodyGrad.addColorStop(0.5, "rgba(34,197,94,1)");
      bodyGrad.addColorStop(0.8, "rgba(16,185,129,1)");
      bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = bodyGrad;
      ctx.fill();

      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.34,
        py - r * 0.38,
        Math.max(1.8, r * 0.26),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.08,
        py - r * 0.14,
        Math.max(1.2, r * 0.11),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px + r * 0.28,
        py + r * 0.34,
        Math.max(1.4, r * 0.18),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(0,0,0,0.10)";
      ctx.fill();

      ctx.restore();
    },
  };
}

export default function PitchPlotAudio({
  intensity,
  audioRef,
}) {
  const [showDerivative, setShowDerivative] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);

  const xs = useMemo(() => intensity?.pitch?.xs || [], [intensity]);
  const ysRaw = useMemo(() => intensity?.pitch?.ys || [], [intensity]);

  const intensityXs = useMemo(
    () => intensity?.intensity?.xs || [],
    [intensity]
  );

  const intensityYs = useMemo(() => {
    const rawValues = intensity?.intensity?.ys || [];

    return rawValues.map((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    });
  }, [intensity]);

  const hasIntensity =
    intensityXs.length > 0 &&
    intensityYs.some((value) => value != null);

  const { intensityMin, intensityMax } = useMemo(() => {
    const validValues = intensityYs.filter((value) =>
      Number.isFinite(value)
    );

    if (!validValues.length) {
      return {
        intensityMin: 0,
        intensityMax: 1,
      };
    }

    const minimum = Math.min(...validValues);
    const maximum = Math.max(...validValues);
    const range = Math.max(1, maximum - minimum);

    return {
      intensityMin: minimum - range * 0.08,
      intensityMax: maximum + range * 0.08,
    };
  }, [intensityYs]);

  const dYsOriginal = useMemo(
    () => intensity?.pitch_1st_derivative?.ys || [],
    [intensity]
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) {
      displayTimeRef.current = 0;
      setAnimatedTime(0);
      return;
    }

    const syncNow = () => {
      const t = Math.max(0, audio.currentTime);
      displayTimeRef.current = t;
      setAnimatedTime(t);
    };

    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const tick = () => {
      const target = Math.max(0, audio.currentTime);
      const previous = displayTimeRef.current;

      displayTimeRef.current = target;

      if (previous == null || Math.abs(target - previous) > 0.0001) {
        setAnimatedTime(target);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const handleSeeked = () => {
      const t = Math.max(0, audio.currentTime);
      displayTimeRef.current = t;
      setAnimatedTime(t);
    };

    syncNow();

    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("loadedmetadata", handleSeeked);
    audio.addEventListener("ratechange", handleSeeked);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      stop();
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("loadedmetadata", handleSeeked);
      audio.removeEventListener("ratechange", handleSeeked);
    };
  }, [audioRef]);

  const ys = useMemo(() => {
    const out = new Array(ysRaw.length);

    for (let i = 0; i < ysRaw.length; i++) {
      const v = Number(ysRaw[i]);

      // The backend uses a tiny positive baseline (for example 0.001 Hz)
      // for silence. Treat it as unvoiced so tails and pauses become gaps.
      out[i] = isFiniteNum(v) && v >= VOICED_THRESHOLD_HZ ? v : null;
    }

    return out;
  }, [ysRaw]);

  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!xs.length || !ys.length) return { voicedXMin: 0, voicedXMax: 1 };

    let first = -1;
    let last = -1;

    for (let i = 0; i < ys.length; i++) {
      if (ys[i] != null) {
        first = i;
        break;
      }
    }
    for (let i = ys.length - 1; i >= 0; i--) {
      if (ys[i] != null) {
        last = i;
        break;
      }
    }

    if (first === -1 || last === -1) {
      return { voicedXMin: xs[0] ?? 0, voicedXMax: xs[xs.length - 1] ?? 1 };
    }

    return { voicedXMin: Number(xs[first]), voicedXMax: Number(xs[last]) };
  }, [xs, ys]);

  const { yMin, yMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < ys.length; i++) {
      const v = ys[i];
      if (v == null) continue;
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { yMin: 0, yMax: 1 };
    }
    return { yMin: min, yMax: max };
  }, [ys]);

  const yRange = useMemo(() => Math.max(1e-6, yMax - yMin), [yMax, yMin]);

  const cursorX = useMemo(() => {
    if (!xs.length) return null;

    const t = Number(animatedTime);
    if (!isFiniteNum(t)) return null;

    // Do not clamp to either edge. Clamping makes the cursor/ball appear
    // frozen at the first or last voiced frame while silent tails play.
    if (t < voicedXMin || t > voicedXMax) return null;

    return t;
  }, [animatedTime, xs.length, voicedXMin, voicedXMax]);

  const cursorEnvelopePoint = useMemo(() => {
    if (cursorX == null) return null;

    // Only return a point when the current audio time is genuinely voiced.
    // This hides the ball in both leading/trailing silence and internal pauses.
    return interpolateRawVoicedAtTime(xs, ys, cursorX);
  }, [xs, ys, cursorX]);

  const showBall = cursorEnvelopePoint != null;

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const dYsScaled = useMemo(() => {
    if (dYsOriginal.length !== xs.length || !xs.length) return [];
    const absMax =
      Math.max(...dYsOriginal.map((v) => Math.abs(Number(v) || 0))) || 1;

    const scaleFactor = (yRange * 0.5) / absMax;
    const verticalShift = yMin + yRange * 0.25;

    const out = new Array(dYsOriginal.length);
    for (let i = 0; i < dYsOriginal.length; i++) {
      const v = Number(dYsOriginal[i]);
      out[i] = isFiniteNum(v) ? v * scaleFactor + verticalShift : null;
    }
    return out;
  }, [dYsOriginal, xs.length, yMin, yRange]);

  const cursorBallPlugin = useMemo(() => makeCursorBallPlugin(), []);

  const data = useMemo(() => {
    const datasets = [];

    if (hasIntensity) {
      datasets.push({
        label: "Intensity",
        data: intensityXs.map((x, index) => ({
          x: Number(x),
          y: intensityYs[index],
        })),
        yAxisID: "yIntensity",
        borderColor: "rgba(148,163,184,0.45)",
        borderWidth: isMobile ? 1.1 : 1.5,
        tension: isMobile ? 0.25 : 0.42,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        showLine: true,
        spanGaps: true,
        borderCapStyle: "round",
        borderJoinStyle: "round",
        datalabels: { display: false },
      });
    }

    datasets.push({
      label: "Pitch",
      data: xs.map((x, index) => ({
        x,
        y: ys[index],
      })),
      yAxisID: "y",
      borderColor: "rgba(37,99,235,1)",
      borderWidth: isMobile ? 1.75 : 2.25,
      tension: isMobile ? 0.25 : 0.42,
      cubicInterpolationMode: "monotone",
      pointRadius: 0,
      showLine: true,
      spanGaps: false,
      borderCapStyle: "round",
      borderJoinStyle: "round",
      datalabels: { display: false },
    });

    if (showDerivative && dYsScaled.length === xs.length) {
      datasets.push({
        label: "Pitch derivative",
        data: xs.map((x, index) => ({
          x,
          y: dYsScaled[index],
        })),
        yAxisID: "y",
        borderColor: "purple",
        borderDash: [4, 4],
        borderWidth: isMobile ? 1 : 2,
        tension: isMobile ? 0.25 : 0.42,
        pointRadius: 0,
        showLine: true,
        spanGaps: true,
        datalabels: { display: false },
      });
    }

    return { datasets };
  }, [
    xs,
    ys,
    intensityXs,
    intensityYs,
    hasIntensity,
    isMobile,
    showDerivative,
    dYsScaled,
  ]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },
      scales: {
        x: {
          type: "linear",
          min: voicedXMin,
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(0,0,0,0.05)" },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: 0,
          max: yMax + yRange * 0.16,
          grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
          title: {
            display: true,
            text: "Pitch (Hz)",
            font: { size: isMobile ? 12 : 14 },
          },
          ticks: {
            font: { size: isMobile ? 10 : 12 },
            maxTicksLimit: 5,
            callback: (value) => Number(value).toFixed(1),
          },
        },
        yIntensity: {
          type: "linear",
          position: "right",
          display: false,
          min: intensityMin,
          max: intensityMax,
          grid: {
            drawOnChartArea: false,
            drawBorder: false,
          },
          ticks: {
            display: false,
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
        },
        datalabels: { display: false },
        cursorBallPlugin: {
          cursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall,
          isMobile,
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      elements: {
        point: { radius: 0, hoverRadius: 0, hitRadius: 0 },
      },
    };
  }, [
    isMobile,
    voicedXMin,
    voicedXMax,
    yMax,
    yRange,
    intensityMin,
    intensityMax,
    cursorX,
    cursorEnvelopePoint,
    ballRadius,
    showBall,
  ]);

  if (!xs.length) return null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <div
        className={[
          "flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white",
          isMobile ? "h-48" : "h-64",
        ].join(" ")}
      >
        <div className="flex shrink-0 items-center gap-4 border-b border-slate-100 px-3 py-1.5 text-[10px] font-medium">
          <span
            className="inline-flex items-center gap-1.5"
            style={{ color: "rgba(37,99,235,1)" }}
          >
            <span
              className="block w-5"
              style={{ borderTop: "2px solid rgba(37,99,235,1)" }}
              aria-hidden="true"
            />
            Pitch
          </span>

          {hasIntensity && (
            <span
              className="inline-flex items-center gap-1.5"
              style={{ color: "rgba(100,116,139,0.9)" }}
            >
              <span
                className="block w-5"
                style={{
                  borderTop: "1.5px solid rgba(148,163,184,0.45)",
                }}
                aria-hidden="true"
              />
              Intensity
            </span>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-x-auto p-2 pt-1">
          <Line
            data={data}
            options={options}
            plugins={[cursorBallPlugin]}
          />
        </div>
      </div>
    </div>
  );
}